import React, { useState } from 'react';
import { Alert, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import unifiedApi from '../../../utils/unifiedApi';
import { getLeagueOverride, useFeatures } from '../../../contexts/FeatureContext';
import { FEATURE_DEFINITIONS } from './featureDefinitions';
import FeatureOffConfirmDialog from './FeatureOffConfirmDialog';

const FOLLOW = 'follow';

const toSelectValue = (override) => {
  if (override === null) return FOLLOW;
  return override ? 'on' : 'off';
};

const toOverride = (value) => {
  if (value === FOLLOW) return null;
  return value === 'on';
};

/** Per-league feature overrides: follow the organization, or turn a feature on/off just for this league. */
const LeagueFeatureSettings = ({ leagueId, canEdit = false }) => {
  const { flags, isEnabled, setFlags } = useFeatures();
  const [savingKey, setSavingKey] = useState(null);
  const [pendingOff, setPendingOff] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });

  const save = async (feature, override) => {
    setSavingKey(feature.key);
    setMessage({ type: '', text: '' });
    try {
      const updated = await unifiedApi.updateLeagueFeatures(leagueId, { [feature.key]: override });
      setFlags(updated);
      setMessage({ type: 'success', text: `${feature.label} setting saved.` });
    } catch {
      setMessage({ type: 'error', text: `Failed to update ${feature.label}.` });
    } finally {
      setSavingKey(null);
    }
  };

  const handleChange = (feature, value) => {
    const override = toOverride(value);
    const willBeOn = override ?? isEnabled(feature.key);
    if (!willBeOn && isEnabled(feature.key, leagueId)) {
      setPendingOff({ feature, override });
    } else {
      save(feature, override);
    }
  };

  return (
    <Paper sx={{ p: { xs: 2, sm: 3 }, maxWidth: 600, mt: 3 }}>
      <Typography variant="h6" gutterBottom>
        Features
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Choose whether this league follows your organization&apos;s settings or turns a feature on or off just for this league.
      </Typography>

      {message.text && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage({ type: '', text: '' })}>
          {message.text}
        </Alert>
      )}

      <Stack spacing={2}>
        {FEATURE_DEFINITIONS.map((feature) => (
          <TextField
            key={feature.key}
            select
            fullWidth
            label={feature.label}
            value={toSelectValue(getLeagueOverride(flags, feature.key, leagueId))}
            onChange={(e) => handleChange(feature, e.target.value)}
            disabled={!canEdit || savingKey === feature.key}
            helperText={feature.description}
          >
            <MenuItem value={FOLLOW}>Follow organization ({isEnabled(feature.key) ? 'On' : 'Off'})</MenuItem>
            <MenuItem value="on">On</MenuItem>
            <MenuItem value="off">Off</MenuItem>
          </TextField>
        ))}
      </Stack>

      <FeatureOffConfirmDialog
        feature={pendingOff?.feature}
        scopeLabel="for this league"
        onCancel={() => setPendingOff(null)}
        onConfirm={() => {
          const { feature, override } = pendingOff;
          setPendingOff(null);
          save(feature, override);
        }}
      />
    </Paper>
  );
};

export default LeagueFeatureSettings;
